<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2 dq-page-actions">
          <div class="input-icon-end position-relative" style="min-width: 160px">
            <input
              v-model.number="filters.year"
              type="number"
              min="2015"
              max="2099"
              step="1"
              class="form-control"
              :disabled="loading"
              @change="reload"
              placeholder="Year"
            />
            <span class="input-icon-addon"><i class="ti ti-calendar"></i></span>
          </div>

          <div class="input-icon-end position-relative" style="min-width: 240px">
            <input
              v-model.trim="filters.q"
              type="text"
              class="form-control"
              :disabled="loading"
              @keyup.enter="fetchList"
              placeholder="Search name / emp id / email"
            />
            <span class="input-icon-addon"><i class="ti ti-search"></i></span>
          </div>

          <button class="btn btn-white" :disabled="loading" @click="reload">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            Refresh
          </button>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="alert alert-warning">
        The table below shows the latest imported monthly snapshot for the selected year, plus how many months were imported.
      </div>

      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <div>
            <h5 class="mb-1">Imported Legacy P9A ({{ filters.year }})</h5>
            <p class="text-muted mb-0">
              Download employee P9A-style annual tax cards reconstructed from imported monthly archive snapshots.
            </p>
          </div>
        </div>

        <div class="card-body p-0">
          <div v-if="error" class="alert alert-danger m-3 mb-0">{{ error }}</div>

          <div class="custom-datatable-filter table-responsive">
            <LegacyP9ReportTable
              :rows="rows"
              :loading="loading"
              :total="meta.total || 0"
              :page-size="meta.per_page || 25"
              :current="meta.current_page || 1"
              :downloading-ids="downloadingIds"
              :emailing-ids="emailingIds"
              @change="onTableChange"
              @download="onDownload"
              @email="onEmail"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import reportsService from "@/services/reports.service";
import LegacyP9ReportTable from "./legacy-p9-report-table.vue";
import { toast } from "@/utils/toast";

const title = "Legacy P9A Report";
const text = "HR";
const text1 = "Imported Legacy P9A";

const loading = ref(false);
const error = ref("");
const rows = ref([]);
const meta = ref({
  current_page: 1,
  per_page: 25,
  total: 0,
});

const filters = reactive({
  year: new Date().getFullYear(),
  page: 1,
  per_page: 25,
  q: "",
});

const downloadingIds = ref(new Set());
const emailingIds = ref(new Set());

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function fetchList() {
  loading.value = true;
  error.value = "";

  try {
    const { data } = await reportsService.legacyP9List({
      year: filters.year,
      page: filters.page,
      per_page: filters.per_page,
      search: filters.q || undefined,
    });

    const paginated = data?.rows || {};
    rows.value = paginated.data || [];
    meta.value = {
      current_page: paginated.current_page || filters.page,
      per_page: paginated.per_page || filters.per_page,
      total: paginated.total || 0,
    };
  } catch (err) {
    error.value = err?.response?.data?.message || "Failed to load imported legacy P9A.";
  } finally {
    loading.value = false;
  }
}

function onTableChange(pagination) {
  filters.page = pagination.current;
  filters.per_page = pagination.pageSize;
  fetchList();
}

async function onDownload(record) {
  downloadingIds.value.add(record.id);
  try {
    const res = await reportsService.legacyP9Download(record.id, filters.year);
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Legacy_P9A_${record.emp_code || record.id}_${filters.year}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast("Failed to download legacy P9A", "error");
  } finally {
    downloadingIds.value.delete(record.id);
  }
}

async function onEmail(record) {
  emailingIds.value.add(record.id);
  try {
    await reportsService.legacyP9Email(record.id, filters.year);
    toast("Imported legacy P9A emailed to employee.", "success");
  } catch (err) {
    toast("Failed to email imported legacy P9A", "error");
  } finally {
    emailingIds.value.delete(record.id);
  }
}

async function reload() {
  filters.page = 1;
  await fetchList();
}

onMounted(fetchList);
</script>

<style scoped>
@media (max-width: 767.98px) {
  .dq-page-actions {
    align-items: stretch !important;
  }

  .dq-page-actions > * {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}
</style>


